import { Txt } from 'components/Txt';
import * as Updates from 'expo-updates';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { doToast, RESET_DEV } from 'store/actions';

import { Button } from './Button';
import { SeparatorHorizontal } from './SeparatorHorizontal';
import { Card } from './Card';
import { CardRow } from './CardRow';
import { ICONS } from './Icon';
import {
  SUPPORT_EMAIL,
  SUPPORT_WHATSAPP,
  SUPPORT_WHATSAPP_URL,
} from 'types/types';
import { CardGeneric } from './CardGeneric';
import { useSelector } from 'hooks/useSelector';
import { selectUserReference } from 'store/auth/selectors';
import * as Clipboard from 'expo-clipboard';
import { openUrl } from 'lib/riskbase';
import {
  APP_VERSION,
  getDeviceLineBreak,
  getInfoDump,
} from 'lib/getDeviceInformation';

const LINE_BREAK = getDeviceLineBreak();

export const ErrorBoundaryAppFallback: React.FC = () => {
  const dispatch = useDispatch();
  const userReference = useSelector(selectUserReference);

  const reloadApp = () => {
    if (__DEV__) {
      dispatch(RESET_DEV());
      setTimeout(() => {
        if (Platform.OS === 'web') {
          window.location.reload();
        } else {
          Updates.reloadAsync();
        }
      }, 1000);
    } else if (Platform.OS === 'web') {
      window.location.reload();
    } else {
      Updates.reloadAsync();
    }
  };

  const handlePressEmail = async (copy: boolean) => {
    if (copy) {
      const copied = await Clipboard.setStringAsync(SUPPORT_EMAIL);
      if (copied) {
        dispatch(doToast('Email Copied'));
      }
      return;
    }
    openUrl(
      `mailto:${SUPPORT_EMAIL}?` +
        `subject=Support Request (User: RB-${userReference}, Version: ${APP_VERSION})&` +
        'body=' +
        `Please tell us about your issue.${LINE_BREAK}${LINE_BREAK}---${LINE_BREAK} ` +
        `${getInfoDump(userReference ?? 'Unknown')}`,
    );
  };

  const handlePressWhatsapp = async (copy: boolean) => {
    if (copy) {
      const copied = await Clipboard.setStringAsync(SUPPORT_WHATSAPP);
      if (copied) {
        dispatch(doToast('WhatsApp Copied'));
        return;
      }
    }
    openUrl(SUPPORT_WHATSAPP_URL);
  };

  return (
    <View style={[StyleSheet.absoluteFill, styles.fallbackView]}>
      <View style={styles.fallbackViewInner}>
        <Txt size="large" weight="bold" style={styles.text}>
          {'Sorry, something went wrong'}
        </Txt>
        <Txt style={styles.text}>Please restart RiskBase to continue</Txt>
        <Button
          onPress={reloadApp}
          text="Restart RiskBase"
          style={styles.fallbackButton}
        />
        <SeparatorHorizontal
          marginVertical={theme.paddingVertical}
          marginHorizontal={theme.padding}
          style={{ alignSelf: 'stretch' }}
        />
        <CardGeneric>
          <Txt style={{ textAlign: 'center' }}>
            If this message keeps appearing, get in touch with our support team
          </Txt>
          <Txt style={{ textAlign: 'center' }}>
            Include as much detail as possible;{'\n'}describe what you were
            doing before this happened and attach screenshots if you can
          </Txt>
        </CardGeneric>
        <SeparatorHorizontal
          marginVertical={theme.paddingVertical}
          marginHorizontal={theme.padding}
          style={{ alignSelf: 'stretch' }}
        />
        <View>
          <Card>
            <CardRow
              icon={ICONS.AtLight}
              iconSize={theme.grid * 3}
              hideMenuButton
              showMenuOnPress
              menuOptions={[
                {
                  text: 'Start New Email',
                  onPress: () => handlePressEmail(false),
                  icon: ICONS.AtLight,
                },
                {
                  text: 'Copy Email Address',
                  onPress: () => handlePressEmail(true),
                  icon: ICONS.CopyLight,
                },
              ]}
            >
              <View style={{ alignItems: 'flex-start' }}>
                <Txt size="large">Email us at</Txt>
                <Txt
                  size="normal"
                  weight="bold"
                  style={{ color: theme.colors.blue }}
                >
                  {SUPPORT_EMAIL}
                </Txt>
              </View>
            </CardRow>
            <CardRow
              icon={ICONS.WhatsappLight}
              iconSize={theme.grid * 3}
              hideMenuButton
              showMenuOnPress
              menuOptions={[
                {
                  text: 'Open WhatsApp',
                  onPress: () => handlePressWhatsapp(false),
                  icon: ICONS.WhatsappLight,
                },
                {
                  text: 'Copy WhatsApp Number',
                  onPress: () => handlePressWhatsapp(true),
                  icon: ICONS.CopyLight,
                },
              ]}
            >
              <View style={{ alignItems: 'flex-start' }}>
                <Txt size="large">WhatsApp us on</Txt>
                <Txt
                  size="normal"
                  weight="bold"
                  style={{ color: theme.colors.blue }}
                >
                  {SUPPORT_WHATSAPP}
                </Txt>
              </View>
            </CardRow>
          </Card>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  fallbackButton: {
    alignSelf: 'center',
    marginTop: theme.grid * 2,
  },
  fallbackView: {
    // backgroundColor: 'white',
    alignSelf: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    // flex: 1,
    // justifyContent: 'center',
    zIndex: 9999,
  },
  fallbackViewInner: {
    // alignItems: 'center',
    maxWidth: 300,
  },
  text: {
    textAlign: 'center',
  },
});
